import { Component, inject } from '@angular/core';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterOutlet } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { AnimationDuration, getAnimationAppearDisappearWithHeight } from '@examdojo/animation';
import { FeatureFlagDirective, FeatureFlagService } from '@examdojo/core/feature-flag';
import { IconComponent } from '@examdojo/core/icon';
import { DebuggingContextService, DebuggingWindowComponent } from '@examdojo/debugging';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { QuestionAttemptStoreModel } from '@examdojo/question';
import { InformativeBannerComponent } from '@examdojo/ui/informative-banner';
import { AppLoaderComponent, RoutingLoadingService } from '@examdojo/ui/loading';
import { IonApp } from '@ionic/angular/standalone';
import { translateSignal } from '@jsverse/transloco';
import { distinctUntilChanged, map } from 'rxjs';
import { register as registerSwiper } from 'swiper/element/bundle';
import { DatasetUploadDialogService } from './debugging/dataset-upload-dialog/dataset-upload-dialog.service';
import { APP_EFFECTS } from './effects';
import { IntercomService } from './intercom/intercom.service';

registerSwiper();

@Component({
  selector: 'dojo-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    IonApp,
    RouterOutlet,
    DebuggingWindowComponent,
    FeatureFlagDirective,
    MatProgressBar,
    IconComponent,
    MatTooltip,
    AppLoaderComponent,
    InformativeBannerComponent,
  ],
  providers: [...APP_EFFECTS],
  animations: [
    getAnimationAppearDisappearWithHeight({ enter: AnimationDuration.Medium, leave: AnimationDuration.Long }),
  ],
})
export class AppComponent {
  constructor(
    private readonly routingLoadingService: RoutingLoadingService,
    private readonly debuggingContextService: DebuggingContextService,
    private readonly datasetUploadDialogService: DatasetUploadDialogService,
  ) {}

  private readonly instantiatedServices = [
    ...APP_EFFECTS,
    IntercomService,
    FeatureFlagService<ExamdojoFeatureFlags>,
  ].map((service) => inject(service));

  /**
   * By design, Transloco will only load the scopes when they are actually used, not when being provided.
   * This comes with the limitation of not being able to translate programmatically when the scope is not loaded, typically in toasts.
   * Do not remove.
   * @see https://github.com/jsverse/transloco/issues/820
   */
  protected readonly preloadExamdojoTranslationsLoading = translateSignal('');

  protected readonly ExamDojoFeatureFlag = ExamDojoFeatureFlag;

  readonly state = connectState({
    showFullScreenLoader: this.routingLoadingService.showFullScreenLoader$,
    showLoadingBar: this.routingLoadingService.showLoadingBar$,
    gradedAttemptId: this.debuggingContextService.context$.pipe(
      map((context) => {
        const attempt = context['Attempt'] as QuestionAttemptStoreModel | undefined;
        return attempt?.id && attempt?.status === 'GRADED' ? attempt.id : null;
      }),
      distinctUntilChanged(),
    ),
  });

  openDatasetUploadDialog(attemptId: QuestionAttemptStoreModel['id']) {
    this.datasetUploadDialogService.openDialog({ input: { attemptId } });
  }
}
